import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, ReceiptText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PageHeader } from "@/components/shared/PagePrimitives";
import { DocUploadOverlay } from "@/components/doc-upload/DocUploadOverlay";
import { useDocPicker } from "@/components/shared/DocPicker";
import { useAuth } from "@/context/AuthContext";
import { formatMoney } from "@/lib/format";
import { WarehouseKpis } from "@/components/warehouse/WarehouseKpis";
import { WarehouseToolbar } from "@/components/warehouse/WarehouseToolbar";
import { WarehouseSidebar } from "@/components/warehouse/WarehouseSidebar";
import { StockList } from "@/components/warehouse/StockList";
import { DocQueue } from "@/components/warehouse/DocQueue";
import { MovementList } from "@/components/warehouse/MovementList";
import { IssueDialog } from "@/components/warehouse/dialogs/IssueDialog";
import { TransferDialog } from "@/components/warehouse/dialogs/TransferDialog";
import { WriteoffDialog } from "@/components/warehouse/dialogs/WriteoffDialog";
import { MinQtyDialog } from "@/components/warehouse/dialogs/MinQtyDialog";
import { ManualReceiptDialog } from "@/components/warehouse/dialogs/ManualReceiptDialog";
import { InventoryDialog } from "@/components/warehouse/dialogs/InventoryDialog";
import { ReturnDialog } from "@/components/warehouse/dialogs/ReturnDialog";

const fmtQty = (n) => (n == null ? "0" : Number(n).toLocaleString("cs-CZ", { maximumFractionDigits: 3 }));
const plur = (n, a, b, c) => `${n} ${n === 1 ? a : n >= 2 && n <= 4 ? b : c}`;

const OPS_ROLES = ["OWNER", "ASSISTANT", "WAREHOUSE"];
const DAY30 = new Date(Date.now() - 30 * 864e5).toISOString();

// KPI zúžené na vybraný sklad (§6) — počítáno z overview.stock/movements/queue.
function scopedKpis(overview, queue, wid, money) {
  const stock = overview.stock.filter((s) => s.warehouse_id === wid);
  const movs = overview.movements.filter((m) => m.warehouse_id === wid || m.to_warehouse_id === wid);
  const qdocs = (queue.docs || []).filter((d) => d.warehouse_id === wid);
  const low = stock.filter((s) => (s.min_qty || 0) > 0 && (s.qty || 0) < s.min_qty);
  const dead = stock.filter((s) => (s.qty || 0) > 0 && !s.has_movement);
  const queueLines = qdocs.reduce((a, d) => a + (d.lines?.length || 0), 0);
  const k = { stock_items: stock.length, queue_lines: queueLines, low_count: low.length, dead_count: dead.length };
  if (money) {
    k.stock_value = Math.round(stock.reduce((a, s) => a + ((s.qty || 0) > 0 ? s.qty * (s.avg_price || 0) : 0), 0));
    k.queue_value = Math.round(qdocs.reduce((a, d) => a + (d.total || 0), 0));
    let issued = 0, wo = 0;
    movs.forEach((m) => {
      if ((m.created_at || "") < DAY30) return;
      const t = m.total || 0;
      if (m.type === "issue") issued += t;
      else if (m.type === "return") issued -= t;
      else if (m.type === "writeoff") wo += t;
      else if (m.type === "adjustment" && (m.qty || 0) < 0) wo += Math.abs(t);
    });
    k.issued_30d = Math.round(issued);
    k.writeoff_30d = Math.round(wo);
    k.writeoff_pct = issued > 0 ? Math.round((wo / issued) * 100) : 0;
    k.dead_value = Math.round(dead.reduce((a, s) => a + s.qty * (s.avg_price || 0), 0));
  } else {
    let ic = 0, wc = 0;
    movs.forEach((m) => {
      if ((m.created_at || "") < DAY30) return;
      if (m.type === "issue") ic++;
      else if (m.type === "writeoff" || (m.type === "adjustment" && (m.qty || 0) < 0)) wc++;
    });
    k.issued_30d_count = ic;
    k.writeoff_30d_count = wc;
  }
  return k;
}

export default function Warehouse() {
  const { user } = useAuth();
  const money = user?.role !== "MANAGER";
  const canOps = OPS_ROLES.includes(user?.role);

  const [overview, setOverview] = useState(null);
  const [queue, setQueue] = useState({ docs: [], total: 0, count: 0 });
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWh, setSelectedWh] = useState(null);
  const [tab, setTab] = useState("stock");
  // 236: z úvodu „Dochází na skladě“ (?pod=minimum) rovnou položky pod minimem
  const [kpiFilter, setKpiFilter] = useState(() => (new URLSearchParams(window.location.search).get("pod") === "minimum" ? "low" : null)); // null | "low" | "dead"
  const [search, setSearch] = useState("");
  const [upload, setUpload] = useState(null);   // { type, file } — 184: výběr souboru rovnou z menu
  const docPicker = useDocPicker({ onPick: (file, type) => setUpload({ type, file }) });
  const [dialog, setDialog] = useState(null); // {type, item?, mov?}
  const [busy, setBusy] = useState(false);

  const loadAll = useCallback(async () => {
    const [ov, q] = await Promise.all([api.get("/warehouses/overview"), api.get("/documents/stock-queue")]);
    setOverview(ov.data);
    setQueue(q.data);
  }, []);

  useEffect(() => {
    // 220: tým (jen pro dialog Výdej → „převzal") souběžně, stránka na něj nečeká.
    api.get("/team").then((r) => setTeam(r.data)).catch(() => { /* MANAGER apod. */ });
    (async () => {
      try { await loadAll(); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [loadAll]);

  useEffect(() => {
    const onSaved = () => loadAll();
    window.addEventListener("pp-doc-saved", onSaved);
    return () => window.removeEventListener("pp-doc-saved", onSaved);
  }, [loadAll]);

  const selectWh = (wid) => setSelectedWh((cur) => (cur === wid ? null : wid));

  const whMap = useMemo(() => Object.fromEntries((overview?.warehouses || []).map((w) => [w.id, w])), [overview]);
  const canOpWh = (wid) => !!whMap[wid]?.can_operate;

  const run = async (promise, ok) => {
    setBusy(true);
    try {
      const r = await promise;
      toast.success(typeof ok === "function" ? ok(r) : ok);
      await loadAll();
      setDialog(null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const handleSubmit = (f) => {
    const t = dialog.type, item = dialog.item, mov = dialog.mov;
    if (t === "issue")
      return run(api.post("/warehouses/issue", { stock_item_id: item.id, qty: Number(f.qty), received_by: f.received_by, note: f.note, date: f.date, project_id: f.project_id }),
        `Výdej — ${item.warehouse_name}: ${fmtQty(f.qty)} ${item.unit}`);
    if (t === "transfer")
      return run(api.post("/warehouses/transfer", { stock_item_id: item.id, to_warehouse_id: f.to_warehouse_id, qty: Number(f.qty), note: f.note }),
        (r) => `Převod na sklad ${r.data.to_warehouse_name}: ${fmtQty(f.qty)} ${item.unit}`);
    if (t === "writeoff")
      return run(api.post("/warehouses/writeoff", { stock_item_id: item.id, qty: Number(f.qty), note: f.note }),
        `Odpis — ${item.warehouse_name}: ${fmtQty(f.qty)} ${item.unit}`);
    if (t === "minqty")
      return run(api.patch(`/warehouses/items/${item.id}/min-qty`, { min_qty: Number(f.min_qty) }), "Minimální zásoba uložena.");
    if (t === "manual")
      return run(api.post("/warehouses/manual-receive", { warehouse_id: selectedWh, name: f.name, unit: f.unit || "ks", qty: Number(f.qty), unit_price: Number(f.unit_price || 0), note: f.note }),
        (r) => `Naskladněno na sklad ${r.data.warehouse_name}: ${f.name}`);
    if (t === "inventory")
      return run(api.post("/warehouses/inventory", { warehouse_id: selectedWh, counts: f.counts }),
        (r) => (r.data.adjusted ? `Inventura uložena: ${plur(r.data.adjusted, "úprava", "úpravy", "úprav")}` : "Inventura bez rozdílů."));
    if (t === "return")
      return run(api.post("/warehouses/return", { movement_id: mov.id, qty: Number(f.qty), note: f.note, date: f.date }),
        `Vratka na sklad: ${fmtQty(f.qty)} ${mov.unit}`);
  };

  const handleReceive = (doc, line) =>
    run(api.post("/warehouses/receive", { document_id: doc.doc_id, line_id: line?.line_id }),
      (r) => `Naskladněno na sklad ${r.data.warehouse_name}: ${plur(r.data.received, "položka", "položky", "položek")}`);

  const closeUpload = () => { setUpload(null); loadAll(); };

  // Hledání s odkladem (220): psaní nečeká na přefiltrování a překreslení seznamu.
  const s = useDeferredValue(search).trim().toLowerCase();
  const whObj = overview?.warehouses.find((w) => w.id === selectedWh) || null;

  const scopedStock = useMemo(() => {
    if (!overview) return [];
    let list = selectedWh ? overview.stock.filter((x) => x.warehouse_id === selectedWh) : overview.stock;
    if (kpiFilter === "low") list = list.filter((x) => (x.min_qty || 0) > 0 && (x.qty || 0) < x.min_qty);
    if (kpiFilter === "dead") list = list.filter((x) => (x.qty || 0) > 0 && !x.has_movement);
    if (s) list = list.filter((x) => x.name.toLowerCase().includes(s));
    return list;
  }, [overview, selectedWh, kpiFilter, s]);

  const scopedDocs = useMemo(() => {
    let list = selectedWh ? (queue.docs || []).filter((d) => d.warehouse_id === selectedWh) : (queue.docs || []);
    if (s) list = list.filter((d) => (d.number || "").toLowerCase().includes(s) || (d.supplier || "").toLowerCase().includes(s) || d.lines.some((li) => (li.name || "").toLowerCase().includes(s)));
    return list;
  }, [queue, selectedWh, s]);

  const scopedMovs = useMemo(() => {
    if (!overview) return [];
    let list = selectedWh ? overview.movements.filter((m) => m.warehouse_id === selectedWh || m.to_warehouse_id === selectedWh) : overview.movements;
    if (s) list = list.filter((m) => (m.name || "").toLowerCase().includes(s));
    return list;
  }, [overview, selectedWh, s]);

  // Počty v záložkách (220): jen při změně dat nebo skladu, ne při každém renderu.
  const counts = useMemo(() => (overview ? {
    stock: (selectedWh ? overview.stock.filter((x) => x.warehouse_id === selectedWh) : overview.stock).length,
    queue: (selectedWh ? (queue.docs || []).filter((d) => d.warehouse_id === selectedWh) : (queue.docs || [])).reduce((a, d) => a + d.lines.length, 0),
    movements: (selectedWh ? overview.movements.filter((m) => m.warehouse_id === selectedWh || m.to_warehouse_id === selectedWh) : overview.movements).length,
  } : null), [overview, queue, selectedWh]);

  if (loading || !overview) return <div className="text-muted-foreground" data-testid="warehouse-loading">Načítám…</div>;

  const kpis = selectedWh ? scopedKpis(overview, queue, selectedWh, money) : overview.kpis;
  const scopeValue = money
    ? (selectedWh ? (whObj?.total_value ?? 0) : overview.warehouses.reduce((a, w) => a + (w.total_value || 0), 0))
    : null;

  const onKpiFilter = (f) => { setKpiFilter((cur) => (cur === f ? null : f)); setTab("stock"); };

  const uploadAction = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="h-11 rounded-full" data-testid="upload-doc-btn"><Upload size={16} className="mr-1.5" /> Nahrát doklad</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => docPicker.open("faktura")} data-testid="upload-faktura"><FileText size={15} className="mr-2 text-brand" /> Faktura</DropdownMenuItem>
        <DropdownMenuItem onClick={() => docPicker.open("uctenka")} data-testid="upload-uctenka"><ReceiptText size={15} className="mr-2 text-brand" /> Účtenka</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const needWh = () => toast.error("Nejdřív vlevo vyber sklad, kam se má přijímat.");

  return (
    <div className="space-y-5 pb-24 md:pb-0" data-testid="warehouse-page">
      <PageHeader title="Sklad" subtitle="Příjem z dokladů, výdej na zakázky, převody a inventura." actions={uploadAction} />

      <WarehouseKpis kpis={kpis} money={money} activeFilter={kpiFilter} onFilter={onKpiFilter} onQueue={() => setTab("queue")} />

      <WarehouseToolbar tab={tab} onTab={setTab} counts={counts} search={search} onSearch={setSearch}
        canOps={canOps} onManual={() => (selectedWh ? setDialog({ type: "manual" }) : needWh())}
        onInventory={() => (selectedWh ? setDialog({ type: "inventory" }) : needWh())} />

      <div className="grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-6">
        <WarehouseSidebar warehouses={overview.warehouses} selected={selectedWh} onSelect={selectWh} />

        <div className="bg-card border border-border rounded-[20px] overflow-hidden" data-testid="warehouse-panel">
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h2 className="font-bold text-lg" data-testid="panel-title">{whObj ? whObj.name : "Všechny sklady"}</h2>
              {scopeValue != null && <span className="text-sm text-muted-foreground">Hodnota: <b className="text-foreground">{formatMoney(scopeValue)}</b></span>}
            </div>
          </div>

          {tab === "stock" && <StockList items={scopedStock} showWarehouse={!selectedWh} money={money}
            canOp={canOpWh} onIssue={(it) => setDialog({ type: "issue", item: it })}
            onTransfer={(it) => setDialog({ type: "transfer", item: it })}
            onWriteoff={(it) => setDialog({ type: "writeoff", item: it })}
            onMin={(it) => setDialog({ type: "minqty", item: it })} />}
          {tab === "queue" && <DocQueue docs={scopedDocs} money={money}
            canReceive={canOps && !!selectedWh} busy={busy} onReceive={handleReceive} />}
          {tab === "movements" && <MovementList movements={scopedMovs} showWarehouse={!selectedWh} money={money}
            canOp={canOpWh} onReturn={(m) => setDialog({ type: "return", mov: m })} />}
        </div>
      </div>

      {dialog?.type === "issue" && (
        <IssueDialog item={dialog.item} warehouse={whMap[dialog.item.warehouse_id]} team={team}
          showProjectSelect={user?.role === "MANAGER" && whMap[dialog.item.warehouse_id]?.type === "central"}
          projects={overview.warehouses.filter((w) => w.type === "project" && w.can_operate && !w.project_trashed).map((w) => ({ id: w.project_id, name: w.name }))}
          busy={busy} onSubmit={handleSubmit} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "transfer" && (
        <TransferDialog item={dialog.item} targets={overview.warehouses.filter((w) => w.id !== dialog.item.warehouse_id && !w.project_trashed)}
          busy={busy} onSubmit={handleSubmit} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "writeoff" && (
        <WriteoffDialog item={dialog.item} warehouse={whMap[dialog.item.warehouse_id]} busy={busy} onSubmit={handleSubmit} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "minqty" && (
        <MinQtyDialog item={dialog.item} busy={busy} onSubmit={handleSubmit} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "manual" && (
        <ManualReceiptDialog warehouse={whMap[selectedWh]} busy={busy} onSubmit={handleSubmit} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "inventory" && (
        <InventoryDialog warehouse={whMap[selectedWh]} stock={overview.stock.filter((x) => x.warehouse_id === selectedWh)}
          busy={busy} onSubmit={handleSubmit} onClose={() => setDialog(null)} />
      )}
      {dialog?.type === "return" && (
        <ReturnDialog mov={dialog.mov} busy={busy} onSubmit={handleSubmit} onClose={() => setDialog(null)} />
      )}

      {docPicker.inputs}
      {upload && <DocUploadOverlay type={upload.type} file={upload.file} onClose={closeUpload} onSaved={() => { window.dispatchEvent(new Event("pp-doc-saved")); }} />}
    </div>
  );
}
